import React, { useState, useEffect, useCallback } from 'react';
import { DragDropContext, Droppable, Draggable } from '@hello-pangea/dnd';
import { apiRequest } from '../../../api/api';
import { 
  ArrowPathIcon,
  ChevronUpIcon,
  ChevronDownIcon,
  DocumentArrowDownIcon
} from '@heroicons/react/24/outline';
import * as XLSX from 'xlsx';
import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement,
} from 'chart.js';
import { Bar, Line, Pie } from 'react-chartjs-2';

// Chart.js'i kaydet
ChartJS.register(
  CategoryScale,
  LinearScale,
  BarElement,
  LineElement,
  PointElement,
  Title,
  Tooltip,
  Legend,
  ArcElement
);

const PivotAnalysis = () => {
  const [data, setData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState(null);

  // Özel scrollbar stilleri
  const customScrollbarStyles = `
    .custom-scrollbar::-webkit-scrollbar {
      height: 6px;
    }
    .custom-scrollbar::-webkit-scrollbar-track {
      background: #f3f4f6;
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #9ca3af;
      border-radius: 3px;
    }
    .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #6b7280;
    }
    .dark .custom-scrollbar::-webkit-scrollbar-track {
      background: #374151;
    }
    .dark .custom-scrollbar::-webkit-scrollbar-thumb {
      background: #6b7280;
    }
    .dark .custom-scrollbar::-webkit-scrollbar-thumb:hover {
      background: #9ca3af;
    }
  `;
  const [pivotConfig, setPivotConfig] = useState({
    rows: [],
    columns: [],
    values: [],
    filters: {}
  });
  const [availableFields, setAvailableFields] = useState([
    { id: 'market_name', name: 'Pazar', type: 'dimension' },
    { id: 'subgroup_name', name: 'Alt Grup', type: 'dimension' },
    { id: 'budgettype_name', name: 'Bütçe Tipi', type: 'dimension' },
    { id: 'budget_year', name: 'Yıl', type: 'dimension' },
    { id: 'budget_month', name: 'Ay', type: 'dimension' },
    { id: 'budget_amount', name: 'Bütçe Tutarı', type: 'measure' },
    { id: 'created_date', name: 'Oluşturulma Tarihi', type: 'dimension' },
    { id: 'maingroup_name', name: 'Ana Grup', type: 'dimension' },
    { id: 'budget_name', name: 'Bütçe Adı', type: 'dimension' },
    // Yeni alanlar - marketing_budget_registry'den
    { id: 'country', name: 'Ülke', type: 'dimension' },
    { id: 'city', name: 'Şehir', type: 'dimension' },
    { id: 'agency', name: 'Acenta', type: 'dimension' },
    { id: 'person_name', name: 'Kişi Adı', type: 'dimension' },
    { id: 'summary', name: 'Özet', type: 'dimension' },
    { id: 'fair_name', name: 'Fuar Adı', type: 'dimension' },
    { id: 'hotel_name', name: 'Otel Adı', type: 'dimension' },
    { id: 'region', name: 'Bölge', type: 'dimension' },
    { id: 'quantity', name: 'Miktar', type: 'measure' },
    { id: 'day_count', name: 'Gün Sayısı', type: 'measure' },
    { id: 'pax', name: 'PAX', type: 'measure' },
    { id: 'pp', name: 'PP', type: 'measure' },
    { id: 'eur_curr', name: 'Euro Kur', type: 'measure' }
  ]);
  const [pivotData, setPivotData] = useState([]);
  const [showFilters, setShowFilters] = useState(true);
  const [filterValues, setFilterValues] = useState({});
  const [viewMode, setViewMode] = useState('table'); // 'table' veya 'both'
  const [chartType, setChartType] = useState('bar'); // 'bar', 'line', 'pie'
  const [filters, setFilters] = useState({
    maingroup: '',
    subgroup: '',
    market: '',
    budgettype: '',
    year: '',
    month: '',
    country: ''
  });
  const [filterOptions, setFilterOptions] = useState({
    maingroups: [],
    subgroups: [],
    markets: [],
    budgettypes: [],
    years: [],
    months: [],
    countries: []
  });

  // API'den veri çek
  const fetchData = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      // Filtre parametrelerini hazırla
      const filterParams = new URLSearchParams();
      
      // Aktif filtreleri ekle
      if (filters.maingroup) filterParams.append('maingroup_name', filters.maingroup);
      if (filters.subgroup) filterParams.append('subgroup_name', filters.subgroup);
      if (filters.market) filterParams.append('market_name', filters.market);
      if (filters.budgettype) filterParams.append('budgettype_name', filters.budgettype);
      if (filters.year) filterParams.append('budget_year', filters.year);
      if (filters.month) filterParams.append('budget_month', filters.month);
      if (filters.country) filterParams.append('country', filters.country);
      
      // URL'i oluştur
      const url = `/marketing-budget/registry-pivot/${filterParams.toString() ? '?' + filterParams.toString() : ''}`;
      
      const result = await apiRequest(url);
      setData(result);
      
      // Filtre değerlerini otomatik oluştur
      const newFilterValues = {};
      availableFields.forEach(field => {
        if (field.type === 'dimension') {
          const uniqueValues = [...new Set(result.map(item => item[field.id]).filter(Boolean))];
          newFilterValues[field.id] = uniqueValues;
        }
      });
      setFilterValues(newFilterValues);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, [availableFields, filters]);

  // Filtre seçeneklerini çek
  const fetchFilterOptions = useCallback(async () => {
    try {
      const [maingroupsRes, subgroupsRes, marketsRes, typesRes, countriesRes] = await Promise.all([
        apiRequest('/marketing-budget/maingroups/'),
        apiRequest('/marketing-budget/subgroups/'),
        apiRequest('/marketing-budget/markets/'),
        apiRequest('/marketing-budget/types/'),
        apiRequest('/marketing-budget/countries/')
      ]);
      
      setFilterOptions({
        maingroups: maingroupsRes || [],
        subgroups: subgroupsRes || [],
        markets: marketsRes || [],
        budgettypes: typesRes || [],
        years: Array.from({ length: 10 }, (_, i) => 2020 + i),
        months: Array.from({ length: 12 }, (_, i) => i + 1),
        countries: countriesRes || []
      });
    } catch (error) {
      // Silently handle error
    }
  }, []);

  useEffect(() => {
    fetchData();
    fetchFilterOptions();
  }, [fetchData, fetchFilterOptions]);

  // Filtreler değiştiğinde otomatik yenile
  useEffect(() => {
    if (Object.values(filters).some(value => value !== '')) {
      fetchData();
    }
  }, [filters, fetchData]);

  // Drag & Drop işlemleri
  const handleDragEnd = (result) => {
    const { destination, source, draggableId } = result;

    if (!destination) return;
    if (destination.droppableId === source.droppableId && destination.index === source.index) return;

    const field = availableFields.find(f => f.id === draggableId);
    if (!field) return;

    const newConfig = { ...pivotConfig };

    // Aynı alan içinde taşıma (yeniden sıralama)
    if (destination.droppableId === source.droppableId) {
      if (destination.droppableId === 'rows') {
        const newRows = Array.from(newConfig.rows);
        const [removed] = newRows.splice(source.index, 1);
        newRows.splice(destination.index, 0, removed);
        newConfig.rows = newRows;
      } else if (destination.droppableId === 'columns') {
        const newColumns = Array.from(newConfig.columns);
        const [removed] = newColumns.splice(source.index, 1);
        newColumns.splice(destination.index, 0, removed);
        newConfig.columns = newColumns;
      } else if (destination.droppableId === 'values') {
        const newValues = Array.from(newConfig.values);
        const [removed] = newValues.splice(source.index, 1);
        newValues.splice(destination.index, 0, removed);
        newConfig.values = newValues;
      }
    } else {
      // Farklı alanlar arası taşıma
      // Mevcut alanı tüm alanlardan kaldır
    newConfig.rows = newConfig.rows.filter(id => id !== draggableId);
    newConfig.columns = newConfig.columns.filter(id => id !== draggableId);
    newConfig.values = newConfig.values.filter(id => id !== draggableId);

    // Hedef konuma ekle
    if (destination.droppableId === 'rows') {
      newConfig.rows.splice(destination.index, 0, draggableId);
    } else if (destination.droppableId === 'columns') {
      newConfig.columns.splice(destination.index, 0, draggableId);
    } else if (destination.droppableId === 'values') {
      newConfig.values.splice(destination.index, 0, draggableId);
    } else if (destination.droppableId === 'available-fields') {
        // Available fields'a geri döndürme - sadece mevcut konumdan kaldır
        // (Alan zaten kaldırıldı, available-fields'da otomatik görünecek)
        // Hiçbir şey yapmaya gerek yok, sadece state'i güncelle
      }
    }

    setPivotConfig(newConfig);
  };

  // Pivot tablo oluştur
  const generatePivotTable = useCallback(() => {
    if (pivotConfig.rows.length === 0 && pivotConfig.columns.length === 0) {
      setPivotData([]);
      return;
    }

    // Filtrelenmiş veri
    let filteredData = data;
    Object.entries(pivotConfig.filters).forEach(([field, values]) => {
      if (values && values.length > 0) {
        filteredData = filteredData.filter(item => values.includes(item[field]));
      }
    });

    // Pivot işlemi
    const pivotMap = new Map();
    
    filteredData.forEach(item => {
      // Row key oluştur
      const rowKey = pivotConfig.rows.map(rowField => item[rowField] || 'N/A').join(' | ');
      
      // Column key oluştur
      const colKey = pivotConfig.columns.length > 0 
        ? pivotConfig.columns.map(colField => item[colField] || 'N/A').join(' | ')
        : 'Toplam';

      if (!pivotMap.has(rowKey)) {
        pivotMap.set(rowKey, {});
      }
      
      // Değerleri topla
      pivotConfig.values.forEach(valueField => {
        const value = parseFloat(item[valueField]) || 0;
        if (!pivotMap.get(rowKey)[colKey]) {
          pivotMap.get(rowKey)[colKey] = {};
        }
        if (!pivotMap.get(rowKey)[colKey][valueField]) {
          pivotMap.get(rowKey)[colKey][valueField] = 0;
        }
        pivotMap.get(rowKey)[colKey][valueField] += value;
      });
    });

    // Sonuçları düzenle
    const result = [];
    const allColumns = new Set();
    
    pivotMap.forEach((rowData, rowKey) => {
      Object.keys(rowData).forEach(colKey => allColumns.add(colKey));
    });

    pivotMap.forEach((rowData, rowKey) => {
      const row = { rowKey, ...rowData };
      allColumns.forEach(colKey => {
        if (!row[colKey]) {
          row[colKey] = {};
          pivotConfig.values.forEach(valueField => {
            row[colKey][valueField] = 0;
          });
        }
      });
      result.push(row);
    });

    setPivotData(result);
  }, [data, pivotConfig]);

  useEffect(() => {
    generatePivotTable();
  }, [generatePivotTable]);

  // Alanları temizle
  const clearField = (fieldId, area) => {
    const newConfig = { ...pivotConfig };
    newConfig[area] = newConfig[area].filter(id => id !== fieldId);
    setPivotConfig(newConfig);
  };

  // Filtre değişikliği
  const handleFilterChange = (field, value) => {
    setPivotConfig(prev => ({
      ...prev,
      filters: {
        ...prev.filters,
        [field]: value
      }
    }));
  };

  // Pivot verilerini grafik formatına çevir
  const convertPivotToChartData = useCallback(() => {
    if (pivotData.length === 0) return null;

    // Tüm sütunları al (rowKey hariç)
    const allColumns = new Set();
    pivotData.forEach(row => {
      Object.keys(row).forEach(key => {
        if (key !== 'rowKey') allColumns.add(key);
      });
    });

    const labels = pivotData.map(row => row.rowKey);
    const datasets = [];

    // Her sütun için dataset oluştur
    Array.from(allColumns).forEach((column, index) => {
      const data = pivotData.map(row => {
        const cellData = row[column];
        if (typeof cellData === 'object') {
          // Eğer obje ise, ilk değeri al
          const values = Object.values(cellData);
          return values.length > 0 ? parseFloat(values[0]) || 0 : 0;
        }
        return parseFloat(cellData) || 0;
      });

      // Renk paleti
      const colors = [
        'rgba(54, 162, 235, 0.8)',
        'rgba(255, 99, 132, 0.8)',
        'rgba(255, 205, 86, 0.8)',
        'rgba(75, 192, 192, 0.8)',
        'rgba(153, 102, 255, 0.8)',
        'rgba(255, 159, 64, 0.8)',
        'rgba(199, 199, 199, 0.8)',
        'rgba(83, 102, 255, 0.8)',
      ];

      datasets.push({
        label: column,
        data: data,
        backgroundColor: colors[index % colors.length],
        borderColor: colors[index % colors.length].replace('0.8', '1'),
        borderWidth: 1,
      });
    });

    return {
      labels: labels,
      datasets: datasets
    };
  }, [pivotData]);

  // Excel export fonksiyonu
  const exportToExcel = useCallback(() => {
    if (pivotData.length === 0) {
      alert('Export edilecek veri bulunamadı!');
      return;
    }

    try {
      // Workbook oluştur
      const wb = XLSX.utils.book_new();

      // 1. TABLO VERİLERİ
      const excelData = [];
      
      // Başlık satırı
      const headers = ['Satır'];
      const allColumns = new Set();
      pivotData.forEach(row => {
        Object.keys(row).forEach(key => {
          if (key !== 'rowKey') allColumns.add(key);
        });
      });
      headers.push(...Array.from(allColumns));
      excelData.push(headers);

      // Veri satırları
      pivotData.forEach(row => {
        const rowData = [row.rowKey];
        Array.from(allColumns).forEach(column => {
          const cellData = row[column];
          if (typeof cellData === 'object') {
            // Eğer obje ise, sadece değerleri al (ön ek olmadan)
            const values = Object.values(cellData).map(value => 
              value?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '0'
            ).join('\n');
            rowData.push(values);
          } else {
            rowData.push(cellData?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '-');
          }
        });
        excelData.push(rowData);
      });

      // Tablo sheet'i oluştur
      const wsTable = XLSX.utils.aoa_to_sheet(excelData);
      XLSX.utils.book_append_sheet(wb, wsTable, 'Pivot Analiz');

      // Sütun genişliklerini ayarla
      const colWidths = [];
      headers.forEach((header, index) => {
        if (index === 0) {
          colWidths.push({ wch: 20 }); // Satır sütunu
        } else {
          colWidths.push({ wch: 15 }); // Diğer sütunlar
        }
      });
      wsTable['!cols'] = colWidths;

      // Excel dosyasını indir
      const fileName = `Pivot_Analiz_${new Date().toISOString().split('T')[0]}.xlsx`;
      XLSX.writeFile(wb, fileName);
    } catch (error) {
      alert('Excel export sırasında hata oluştu!');
    }
  }, [pivotData]);

  // CSV export
  const exportToCSV = () => {
    if (pivotData.length === 0) return;

    const headers = ['Satır', ...Array.from(new Set(pivotData.flatMap(row => Object.keys(row).filter(key => key !== 'rowKey'))))];
    const csvContent = [
      headers.join(','),
      ...pivotData.map(row => {
        const values = headers.slice(1).map(header => {
          const cellData = row[header];
          if (typeof cellData === 'object') {
            return Object.values(cellData).join(' | ');
          }
          return cellData || '';
        });
        return [row.rowKey, ...values].join(',');
      })
    ].join('\n');

    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = 'pivot_analysis.csv';
    a.click();
    URL.revokeObjectURL(url);
  };

  const DroppableArea = ({ area, title, fields }) => (
    <div className="bg-gray-50 dark:bg-gray-700 border border-gray-300 dark:border-gray-600 rounded-lg p-1">
      <div className="flex items-center space-x-2 mb-2">
        <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300">{title}</h4>
        <span className="text-xs text-gray-500 dark:text-gray-400">({fields.length})</span>
      </div>
      
      <Droppable droppableId={area}>
        {(provided, snapshot) => (
          <div
            ref={provided.innerRef}
            {...provided.droppableProps}
            className={`min-h-[50px] p-2 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded ${
              snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'bg-white dark:bg-gray-600'
            }`}
          >
            {fields.map((fieldId, index) => {
              const field = availableFields.find(f => f.id === fieldId);
              return (
                <Draggable key={fieldId} draggableId={fieldId} index={index}>
                  {(provided, snapshot) => (
                        <div
                          ref={provided.innerRef}
                          {...provided.draggableProps}
                          {...provided.dragHandleProps}
                          className={`flex items-center justify-between p-1 mb-1 bg-white dark:bg-gray-500 border border-gray-200 dark:border-gray-400 rounded text-xs cursor-move ${
                            snapshot.isDragging ? 'shadow-lg' : 'hover:shadow-sm'
                          }`}
                        >
                          <div className="flex items-center space-x-1">
                            <span className="text-gray-900 dark:text-white text-xs">{field.name}</span>
                            <span className={`px-0.5 py-0.5 text-xs rounded ${
                              field.type === 'measure' 
                                ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                            }`}>
                              {field.type === 'measure' ? 'Ö' : 'B'}
                            </span>
                          </div>
                          <button
                            onClick={() => clearField(fieldId, area)}
                            className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs"
                          >
                            ✕
                          </button>
                        </div>
                  )}
                </Draggable>
              );
            })}
            {provided.placeholder}
            
            {fields.length === 0 && (
              <div className="text-center text-gray-400 dark:text-gray-500 py-2 text-xs">
                Alanları buraya sürükleyin
              </div>
            )}
          </div>
        )}
      </Droppable>
    </div>
  );

  return (
    <div className="min-h-screen bg-gray-50 dark:bg-gray-900">
      <style dangerouslySetInnerHTML={{ __html: customScrollbarStyles }} />
      <div className="w-full px-4">
        {/* Header */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-2xl font-bold text-gray-900 dark:text-white">
                Pivot Analiz
              </h1>
              <p className="text-sm text-gray-600 dark:text-gray-400 mt-1">
                Marketing bütçe verilerini analiz edin
              </p>
            </div>
            
            <div className="flex items-center space-x-4">
              <div className="flex items-center space-x-2">
                <span className={`text-sm ${viewMode === 'table' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>Sadece Tablo</span>
                <button
                  onClick={() => setViewMode(viewMode === 'table' ? 'both' : 'table')}
                  className="w-10 h-5 bg-gray-300 dark:bg-gray-600 rounded-full relative transition-colors hover:bg-gray-400 dark:hover:bg-gray-500"
                >
                  <div className={`w-4 h-4 bg-white dark:bg-gray-200 rounded-full absolute top-0.5 transition-transform duration-200 ${
                    viewMode === 'both' ? 'translate-x-5' : 'translate-x-0.5'
                  }`}></div>
                </button>
                <span className={`text-sm ${viewMode === 'both' ? 'text-blue-600 dark:text-blue-400 font-medium' : 'text-gray-600 dark:text-gray-400'}`}>Grafik + Tablo</span>
              </div>
              
              {/* Grafik Türü Seçici */}
              <div className="flex items-center space-x-2">
                <span className="text-sm text-gray-600 dark:text-gray-400">Grafik Türü:</span>
                <select
                  value={chartType}
                  onChange={(e) => setChartType(e.target.value)}
                  className="text-sm border border-gray-300 dark:border-gray-600 rounded px-2 py-1 bg-white dark:bg-gray-700 text-gray-900 dark:text-white"
                >
                  <option value="bar">Bar Grafik</option>
                  <option value="line">Çizgi Grafik</option>
                  <option value="pie">Pasta Grafik</option>
                </select>
              </div>
              
              <div className="flex items-center space-x-2">
                <button
                  onClick={exportToCSV}
                  disabled={pivotData.length === 0}
                  className="px-3 py-1.5 bg-blue-600 text-white rounded text-sm hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed"
                >
                  CSV İndir
                </button>
                <button
                  onClick={fetchData}
                  disabled={loading}
                  className="px-3 py-1.5 bg-gray-600 text-white rounded text-sm hover:bg-gray-700 disabled:opacity-50"
                >
                  Yenile
                </button>
              </div>
            </div>
          </div>
        </div>

        {/* Filtreler */}
        <div className="bg-white dark:bg-gray-800 border-b border-gray-200 dark:border-gray-700">
          <div className="px-6 py-4 border-b border-gray-100 dark:border-gray-700">
            <div className="flex items-center justify-between">
              <div className="flex items-center space-x-2">
                <span className="font-medium text-gray-900 dark:text-white">Filtreler</span>
                {(() => {
                  const activeFiltersCount = Object.values(filters).filter(value => value !== '').length;
                  return activeFiltersCount > 0 ? (
                    <span className="px-2 py-1 bg-blue-100 dark:bg-blue-900/30 text-blue-700 dark:text-blue-400 text-xs rounded-full">
                      {activeFiltersCount} aktif
                    </span>
                  ) : null;
                })()}
                {showFilters ? (
                  <ChevronUpIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                ) : (
                  <ChevronDownIcon className="h-4 w-4 text-gray-500 dark:text-gray-400" />
                )}
              </div>
              <div className="flex items-center space-x-3">
                <button
                  onClick={() => {
                    setFilters({
                      maingroup: '',
                      subgroup: '',
                      market: '',
                      budgettype: '',
                      year: '',
                      month: '',
                      country: ''
                    });
                  }}
                  className="text-sm text-red-600 dark:text-red-400 hover:text-red-800 dark:hover:text-red-300"
                >
                  Filtreleri Temizle
                </button>
                <button
                  onClick={() => setShowFilters(!showFilters)}
                  className="text-sm text-blue-600 dark:text-blue-400 hover:text-blue-800 dark:hover:text-blue-300"
                >
                  {showFilters ? 'Gizle' : 'Göster'}
                </button>
              </div>
            </div>
          </div>
          
          {showFilters && (
            <div className="px-6 py-6 bg-gray-50 dark:bg-gray-700/50">
              <div className="grid grid-cols-7 gap-6">
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Ana Grup</label>
                  <select
                    value={filters.maingroup}
                    onChange={(e) => setFilters(prev => ({ 
                      ...prev, 
                      maingroup: e.target.value,
                      subgroup: '' // Ana grup değiştiğinde alt grubu temizle
                    }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.maingroups.map(item => (
                      <option key={item.id} value={item.maingroup_name}>{item.maingroup_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Alt Grup</label>
                  <select
                    value={filters.subgroup}
                    onChange={(e) => setFilters(prev => ({ ...prev, subgroup: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.subgroups
                      .filter(item => !filters.maingroup || item.maingroup_name === filters.maingroup)
                      .map(item => (
                        <option key={item.id} value={item.subgroup_name}>{item.subgroup_name}</option>
                      ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Pazar</label>
                  <select
                    value={filters.market}
                    onChange={(e) => setFilters(prev => ({ ...prev, market: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.markets.map(item => (
                      <option key={item.id} value={item.budget_market_name}>{item.budget_market_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Ülke</label>
                  <select
                    value={filters.country}
                    onChange={(e) => setFilters(prev => ({ ...prev, country: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.countries.map(item => (
                      <option key={item.id} value={item.country_name}>{item.country_name}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Bütçe Tipi</label>
                  <select
                    value={filters.budgettype}
                    onChange={(e) => setFilters(prev => ({ ...prev, budgettype: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.budgettypes.map(item => (
                      <option key={item.id} value={item.budget_type}>{item.budget_type}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Yıl</label>
                  <select
                    value={filters.year}
                    onChange={(e) => setFilters(prev => ({ ...prev, year: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.years.map(year => (
                      <option key={year} value={year}>{year}</option>
                    ))}
                  </select>
                </div>
                
                <div>
                  <label className="block text-xs font-medium text-gray-700 dark:text-gray-300 mb-2">Ay</label>
                  <select
                    value={filters.month}
                    onChange={(e) => setFilters(prev => ({ ...prev, month: e.target.value }))}
                    className="w-full px-3 py-2.5 border border-gray-300 dark:border-gray-600 rounded-lg text-sm bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-blue-500 focus:border-blue-500"
                  >
                    <option value="">Tümü</option>
                    {filterOptions.months.map(month => (
                      <option key={month} value={month}>{month}</option>
                    ))}
                  </select>
                </div>
              </div>
            </div>
          )}
        </div>

        {error && (
          <div className="mt-4 bg-red-50 dark:bg-red-900/20 border border-red-200 dark:border-red-800 text-red-700 dark:text-red-400 px-4 py-3 rounded">
            {error}
          </div>
        )}

        {/* Drag & Drop Alanı */}
        <div className="bg-white dark:bg-gray-800 mt-4 rounded-lg border border-gray-200 dark:border-gray-700">
          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="p-4">
              {/* Mevcut Alanlar */}
              <div className="mb-4">
                <Droppable droppableId="available-fields" direction="horizontal">
                  {(provided, snapshot) => {
                    // Sadece kullanılmayan alanları göster (rows, columns, values'de olmayanlar)
                    const usedFieldIds = [
                      ...pivotConfig.rows,
                      ...pivotConfig.columns,
                      ...pivotConfig.values
                    ];
                    const unusedFields = availableFields.filter(
                      field => !usedFieldIds.includes(field.id)
                    );
                    
                    return (
                    <div
                      ref={provided.innerRef}
                      {...provided.droppableProps}
                      className={`flex flex-wrap gap-1 min-h-[30px] p-0.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded overflow-x-auto custom-scrollbar ${
                        snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : ''
                      }`}
                    >
                        {unusedFields.map((field, index) => (
                        <Draggable key={field.id} draggableId={field.id} index={index}>
                          {(provided, snapshot) => (
                            <div
                              ref={provided.innerRef}
                              {...provided.draggableProps}
                              {...provided.dragHandleProps}
                              className={`flex items-center space-x-1 px-2 py-1 bg-gray-100 dark:bg-gray-600 border border-gray-300 dark:border-gray-500 rounded cursor-move text-xs ${
                                snapshot.isDragging ? 'shadow-lg bg-white dark:bg-gray-500' : 'hover:bg-gray-200 dark:hover:bg-gray-500'
                              }`}
                            >
                              <span className="text-gray-900 dark:text-white text-xs">{field.name}</span>
                              <span className={`px-0.5 py-0.5 text-xs rounded ${
                                field.type === 'measure' 
                                  ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                  : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                              }`}>
                                {field.type === 'measure' ? 'Ö' : 'B'}
                              </span>
                            </div>
                          )}
                        </Draggable>
                      ))}
                      {provided.placeholder}
                    </div>
                    );
                  }}
                </Droppable>
              </div>

              {/* Pivot Alanları - Yeni Düzen */}
              <div className="space-y-3">
                {/* Üst - Değerler + Sütunlar */}
                <div className="grid grid-cols-12 gap-1">
                  {/* Sol - Değerler (Küçük) */}
                  <div className="col-span-1">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Değerler ({pivotConfig.values.length})</h4>
                      <Droppable droppableId="values">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[30px] p-0.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded overflow-x-auto custom-scrollbar ${
                              snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-50 dark:bg-gray-600'
                            }`}
                          >
                            <div className="flex gap-1 min-w-max">
                              {pivotConfig.values.map((fieldId, index) => {
                                const field = availableFields.find(f => f.id === fieldId);
                                return (
                                  <Draggable key={fieldId} draggableId={fieldId} index={index}>
                                    {(provided, snapshot) => (
                                        <div
                                          ref={provided.innerRef}
                                          {...provided.draggableProps}
                                          {...provided.dragHandleProps}
                                          className={`flex items-center justify-between p-1 bg-white dark:bg-gray-500 border border-gray-200 dark:border-gray-400 rounded text-xs cursor-move ${
                                            snapshot.isDragging ? 'shadow-lg' : 'hover:shadow-sm'
                                          }`}
                                        >
                                        <div className="flex items-center space-x-1">
                                          <span className="text-gray-900 dark:text-white text-xs">{field.name}</span>
                                          <span className={`px-0.5 py-0.5 text-xs rounded ${
                                            field.type === 'measure' 
                                              ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                              : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                          }`}>
                                            {field.type === 'measure' ? 'Ö' : 'B'}
                                          </span>
                                        </div>
                                        <button
                                          onClick={() => clearField(fieldId, 'values')}
                                          className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {pivotConfig.values.length === 0 && (
                                <div className="text-center text-gray-400 dark:text-gray-500 py-2 text-xs whitespace-nowrap">
                                  Değer alanlarını buraya sürükleyin
                                </div>
                              )}
                            </div>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>

                  {/* Sağ - Sütunlar (Geniş) */}
                  <div className="col-span-11">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-1">
                      <h4 className="text-xs font-medium text-gray-700 dark:text-gray-300 mb-1">Sütunlar ({pivotConfig.columns.length})</h4>
                      <Droppable droppableId="columns">
                        {(provided, snapshot) => (
                          <div
                            ref={provided.innerRef}
                            {...provided.droppableProps}
                            className={`min-h-[30px] p-0.5 border-2 border-dashed border-gray-300 dark:border-gray-600 rounded overflow-x-auto custom-scrollbar ${
                              snapshot.isDraggingOver ? 'border-blue-400 bg-blue-50 dark:bg-blue-900/20' : 'bg-gray-50 dark:bg-gray-600'
                            }`}
                          >
                            <div className="flex flex-wrap gap-1">
                              {pivotConfig.columns.map((fieldId, index) => {
                                const field = availableFields.find(f => f.id === fieldId);
                                return (
                                  <Draggable key={fieldId} draggableId={fieldId} index={index}>
                                    {(provided, snapshot) => (
                                      <div
                                        ref={provided.innerRef}
                                        {...provided.draggableProps}
                                        {...provided.dragHandleProps}
                                        className={`flex items-center space-x-1 px-2 py-1 bg-white dark:bg-gray-500 border border-gray-200 dark:border-gray-400 rounded text-xs cursor-move ${
                                          snapshot.isDragging ? 'shadow-lg' : 'hover:shadow-sm'
                                        }`}
                                      >
                                        <span className="text-gray-900 dark:text-white text-xs">{field.name}</span>
                                        <span className={`px-0.5 py-0.5 text-xs rounded ${
                                          field.type === 'measure' 
                                            ? 'bg-green-100 text-green-700 dark:bg-green-900/20 dark:text-green-400'
                                            : 'bg-blue-100 text-blue-700 dark:bg-blue-900/20 dark:text-blue-400'
                                        }`}>
                                          {field.type === 'measure' ? 'Ö' : 'B'}
                                        </span>
                                        <button
                                          onClick={() => clearField(fieldId, 'columns')}
                                          className="text-red-500 dark:text-red-400 hover:text-red-700 dark:hover:text-red-300 text-xs ml-1"
                                        >
                                          ✕
                                        </button>
                                      </div>
                                    )}
                                  </Draggable>
                                );
                              })}
                              {pivotConfig.columns.length === 0 && (
                                <div className="text-center text-gray-400 dark:text-gray-500 py-2 text-xs w-full">
                                  Sütun alanlarını buraya sürükleyin
                                </div>
                              )}
                            </div>
                            {provided.placeholder}
                          </div>
                        )}
                      </Droppable>
                    </div>
                  </div>
                </div>

                {/* Ana İçerik - Satırlar + Sonuç */}
                <div className="grid grid-cols-12 gap-2">
                  {/* Sol - Satırlar */}
                  <div className="col-span-1">
                    <DroppableArea
                      area="rows"
                      title="Satırlar"
                      fields={pivotConfig.rows}
                    />
                  </div>
                  
                  {/* Sağ - Sonuç Bölümü (Geniş) */}
                  <div className="col-span-11">
                    <div className="bg-gray-50 dark:bg-gray-700 rounded-lg p-4 h-full">
                      <div className="flex justify-between items-center mb-4">
                        <h4 className="text-lg font-medium text-gray-700 dark:text-gray-300">Sonuç</h4>
                        {pivotData.length > 0 && (
                          <button
                            onClick={exportToExcel}
                            className="flex items-center gap-2 px-3 py-2 bg-green-600 hover:bg-green-700 text-white text-sm font-medium rounded-md transition-colors"
                            title="Excel'e Export Et"
                          >
                            <DocumentArrowDownIcon className="h-4 w-4" />
                            Excel Export
                          </button>
                        )}
                      </div>
                      
                      {loading ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                          <ArrowPathIcon className="h-8 w-8 mx-auto mb-2 animate-spin" />
                          <span className="text-sm">Yükleniyor...</span>
                        </div>
                      ) : pivotData.length === 0 ? (
                        <div className="p-8 text-center text-gray-500 dark:text-gray-400">
                          <span className="text-sm">
                            {pivotConfig.rows.length === 0 && pivotConfig.columns.length === 0 
                              ? 'Rapor oluşturmak için alanları sürükleyin'
                              : 'Seçilen kriterlere uygun veri bulunamadı'
                            }
                          </span>
                        </div>
                      ) : (
                        <div className="space-y-6">
                          {/* Grafik Bölümü - Sadece 'both' modunda göster */}
                          {viewMode === 'both' && (
                            <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                              <h5 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Grafik Görünümü</h5>
                              <div className="w-full h-[400px] flex items-center justify-center">
                                {convertPivotToChartData() ? (
                                  <div className="w-full h-full">
                                    {chartType === 'bar' && (
                                      <Bar 
                                        data={convertPivotToChartData()} 
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            title: {
                                              display: true,
                                              text: 'Pivot Analiz - Bar Grafik',
                                              color: '#374151',
                                              font: { size: 14 }
                                            },
                                            legend: {
                                              position: 'top',
                                              labels: {
                                                color: '#374151',
                                                usePointStyle: true
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              ticks: {
                                                color: '#374151'
                                              },
                                              grid: {
                                                color: '#E5E7EB'
                                              }
                                            },
                                            x: {
                                              ticks: {
                                                color: '#374151',
                                                maxRotation: 45
                                              },
                                              grid: {
                                                color: '#E5E7EB'
                                              }
                                            }
                                          }
                                        }} 
                                      />
                                    )}
                                    {chartType === 'line' && (
                                      <Line 
                                        data={convertPivotToChartData()} 
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            title: {
                                              display: true,
                                              text: 'Pivot Analiz - Çizgi Grafik',
                                              color: '#374151',
                                              font: { size: 14 }
                                            },
                                            legend: {
                                              position: 'top',
                                              labels: {
                                                color: '#374151',
                                                usePointStyle: true
                                              }
                                            }
                                          },
                                          scales: {
                                            y: {
                                              beginAtZero: true,
                                              ticks: {
                                                color: '#374151'
                                              },
                                              grid: {
                                                color: '#E5E7EB'
                                              }
                                            },
                                            x: {
                                              ticks: {
                                                color: '#374151',
                                                maxRotation: 45
                                              },
                                              grid: {
                                                color: '#E5E7EB'
                                              }
                                            }
                                          }
                                        }} 
                                      />
                                    )}
                                    {chartType === 'pie' && (
                                      <Pie 
                                        data={convertPivotToChartData()} 
                                        options={{
                                          responsive: true,
                                          maintainAspectRatio: false,
                                          plugins: {
                                            title: {
                                              display: true,
                                              text: 'Pivot Analiz - Pasta Grafik',
                                              color: '#374151',
                                              font: { size: 14 }
                                            },
                                            legend: {
                                              position: 'right',
                                              labels: {
                                                color: '#374151',
                                                usePointStyle: true
                                              }
                                            }
                                          }
                                        }} 
                                      />
                                    )}
                                  </div>
                                ) : (
                                  <div className="text-center text-gray-500 dark:text-gray-400">
                                    <span className="text-sm">Grafik için veri bulunamadı</span>
                                  </div>
                                )}
                              </div>
                            </div>
                          )}

                          {/* Tablo Bölümü - Her zaman göster */}
                          <div className="bg-white dark:bg-gray-800 rounded-lg p-4 border border-gray-200 dark:border-gray-600">
                            <h5 className="text-md font-medium text-gray-700 dark:text-gray-300 mb-4">Tablo Görünümü</h5>
                            <div className="overflow-auto max-h-[400px]">
                              <table className="w-full text-sm">
                                <thead className="bg-gray-100 dark:bg-gray-600 sticky top-0">
                                  <tr>
                                    <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-500">
                                      Satır
                                    </th>
                                    {Array.from(new Set(pivotData.flatMap(row => Object.keys(row).filter(key => key !== 'rowKey')))).map(column => (
                                      <th key={column} className="px-4 py-3 text-left text-xs font-medium text-gray-500 dark:text-gray-400 uppercase tracking-wider border-r border-gray-200 dark:border-gray-500">
                                        {column}
                                      </th>
                                    ))}
                                  </tr>
                                </thead>
                                <tbody className="bg-white dark:bg-gray-800 divide-y divide-gray-200 dark:divide-gray-700">
                                  {pivotData.map((row, index) => (
                                    <tr key={index} className={index % 2 === 0 ? 'bg-white dark:bg-gray-800' : 'bg-gray-50 dark:bg-gray-700'}>
                                      <td className="px-4 py-3 text-xs font-medium text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                                        {row.rowKey}
                                      </td>
                                      {Array.from(new Set(pivotData.flatMap(r => Object.keys(r).filter(key => key !== 'rowKey')))).map(column => (
                                        <td key={column} className="px-4 py-3 text-sm text-gray-900 dark:text-white border-r border-gray-200 dark:border-gray-600">
                                          {row[column] && typeof row[column] === 'object' 
                                            ? Object.entries(row[column]).map(([key, value]) => (
                                                <div key={key} className="text-sm">
                                                  {value?.toLocaleString('tr-TR', { minimumFractionDigits: 2 })}
                                                </div>
                                              ))
                                            : row[column]?.toLocaleString('tr-TR', { minimumFractionDigits: 2 }) || '-'
                                          }
                                        </td>
                                      ))}
                                    </tr>
                                  ))}
                                </tbody>
                              </table>
                            </div>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </DragDropContext>
        </div>

      </div>
    </div>
  );
};

export default PivotAnalysis;
